/**
 * Types and functions for working with 3d vectors
 * @module
 */
import { ANGLE_EPSILON } from "./core";

const { abs, sqrt, atan2 } = Math;

export type Vec3 = [number, number, number];
export type Vec4 = [number, number, number, number];

// These types are used to return a vector without allocating an array. It is assumed that
// the caller will destructure the return value to extract the contents.
export const vec3Result: Vec3 = [0, 0, 0];
export const vec4Result: Vec4 = [0, 0, 0, 0];

export function vec3Copy(vec: Vec3): Vec3 {
  const v: number[] = [];
  return Object.assign(v, vec);
}

export function vec3AddScale(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, scale: number) {
  vec3Result[0] = x0 + scale * x1;
  vec3Result[1] = y0 + scale * y1;
  vec3Result[2] = z0 + scale * z1;
  return vec3Result;
}

export function vec3Add(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  vec3Result[0] = x0 + x1;
  vec3Result[1] = y0 + y1;
  vec3Result[2] = z0 + z1;
  return vec3Result;
}

export function vec3Negate(x: number, y: number, z: number) {
  vec3Result[0] = -x;
  vec3Result[1] = -y;
  vec3Result[2] = -z;

  return vec3Result;
}

export function vec3Sub(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  vec3Result[0] = x0 - x1;
  vec3Result[1] = y0 - y1;
  vec3Result[2] = z0 - z1;
  return vec3Result;
}

export function vec3Scale(x: number, y: number, z: number, s: number) {
  vec3Result[0] = x * s;
  vec3Result[1] = y * s;
  vec3Result[2] = z * s;
  return vec3Result;
}

export function vec3Dist(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  const dx = x0 - x1;
  const dy = y0 - y1;
  const dz = z0 - z1;
  return sqrt(dx * dx + dy * dy + dz * dz);
}

export function vec3DistSquared(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  const dx = x0 - x1;
  const dy = y0 - y1;
  const dz = z0 - z1;
  return dx * dx + dy * dy + dz * dz;
}

export function vec3Normalize(x: number, y: number, z: number) {
  const invlen = 1 / sqrt(x * x + y * y + z * z);
  vec3Result[0] = x * invlen;
  vec3Result[1] = y * invlen;
  vec3Result[2] = z * invlen;
  return vec3Result;
}

export function vec3ClampLength(x: number, y: number, z: number, minLength: number, maxLength: number) {
  const len = sqrt(x * x + y * y + z * z);
  const mult = Math.max(Math.min(len, maxLength), minLength) / len;
  vec3Result[0] = x * mult;
  vec3Result[1] = y * mult;
  vec3Result[2] = z * mult;
  return vec3Result;
}

export function vec3Cross(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  vec3Result[0] = y0 * z1 - z0 * y1;
  vec3Result[1] = z0 * x1 - x0 * z1;
  vec3Result[2] = x0 * y1 - y0 * x1;

  return vec3Result;
}

export function vec3CrossNormalize(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  const [ax, ay, az] = vec3Cross(x0, y0, z0, x1, y1, z1);
  return vec3Normalize(ax, ay, az);
}

export function vec3TriangleNormal(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  normalized: boolean = true,
) {
  const dx0 = x1 - x0;
  const dy0 = y1 - y0;
  const dz0 = z1 - z0;

  const dx1 = x2 - x0;
  const dy1 = y2 - y0;
  const dz1 = z2 - z0;

  const l0 = 1 / vec3Len(dx0, dy0, dz0);
  const l1 = 1 / vec3Len(dx1, dy1, dz1);

  let [nX, nY, nZ] = vec3Cross(dx0 * l0, dy0 * l0, dz0 * l0, dx1 * l1, dy1 * l1, dz1 * l1);
  if (normalized) {
    [nX, nY, nZ] = vec3Normalize(nX, nY, nZ);
  }
  return [nX, nY, nZ];
}

export function vec3RemoveNaNs(x: number, y: number, z: number): Vec3 {
  vec3Result[0] = isNaN(x) ? 0 : x;
  vec3Result[1] = isNaN(y) ? 0 : y;
  vec3Result[2] = isNaN(z) ? 0 : z;
  return vec3Result;
}

export function vec3Dot(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  return x0 * x1 + y0 * y1 + z0 * z1;
}

export function vec3TripleProduct(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
): number {
  const [cx, cy, cz] = vec3Cross(x1, y1, z1, x2, y2, z2);
  return vec3Dot(x0, y0, z0, cx, cy, cz);
}

export function vec3Len(x: number, y: number, z: number): number {
  return sqrt(x * x + y * y + z * z);
}

export function vec3LenSquared(x: number, y: number, z: number): number {
  return x * x + y * y + z * z;
}

// assumes vectors are normalized
export function vec3SolidAngle(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
): number {
  const num = vec3TripleProduct(x0, y0, z0, x1, y1, z1, x2, y2, z2);
  const denom = 1 + vec3Dot(x0, y0, z0, x1, y1, z1) + vec3Dot(x1, y1, z1, x2, y2, z2) + vec3Dot(x2, y2, z2, x0, y0, z0);

  return 2 * atan2(num, denom);
}

export function vec3AngleBetween(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number) {
  const [normx, normy, normz] = vec3Cross(x0, y0, z0, x1, y1, z1);

  return Math.atan2(vec3Len(normx, normy, normz), vec3Dot(x0, y0, z0, x1, y1, z1));
}

export function vec3SignedAngleBetween(
  refNormalX: number,
  refNormalY: number,
  refNormalZ: number,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
) {
  const [normx, normy, normz] = vec3Cross(x0, y0, z0, x1, y1, z1);

  return Math.atan2(vec3Dot(normx, normy, normz, refNormalX, refNormalY, refNormalZ), vec3Dot(x0, y0, z0, x1, y1, z1));
}

export function vec3Lerp(x0: number, y0: number, z0: number, x1: number, y1: number, z1: number, t: number): Vec3 {
  const dx = x1 - x0;
  const dy = y1 - y0;
  const dz = z1 - z0;

  vec3Result[0] = x0 + dx * t;
  vec3Result[1] = y0 + dy * t;
  vec3Result[2] = z0 + dz * t;

  return vec3Result;
}

export function vec3ToString(x: number, y: number, z: number): string {
  return `[${x}, ${y}, ${z}]`;
}

export function vec3SequenceIsTurning(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  x2: number,
  y2: number,
  z2: number,
  epsilon: number = ANGLE_EPSILON,
): boolean {
  const [dx0, dy0, dz0] = vec3Sub(x1, y1, z1, x0, y0, z0);
  const [dx1, dy1, dz1] = vec3Sub(x2, y2, z2, x1, y1, z1);

  return abs(vec3AngleBetween(dx0, dy0, dz0, dx1, dy1, dz1)) > epsilon;
}

export function vec3IsNear(
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  tolerance: number = 0.01,
) {
  return Math.pow(x0 - x1, 2) + Math.pow(y0 - y1, 2) + Math.pow(z0 - z1, 2) <= Math.pow(tolerance, 2);
}

export function vec3Perpendicular(a: Vec3, b: Vec3, angle_tol: number = ANGLE_EPSILON) {
  const [ax, ay, az] = a;
  const [bx, by, bz] = b;
  const dot = vec3Dot(ax, ay, az, bx, by, bz);
  return abs(dot) < angle_tol;
}

export function vec3IsInEdgeBounds(
  x: number,
  y: number,
  z: number,
  x0: number,
  y0: number,
  z0: number,
  x1: number,
  y1: number,
  z1: number,
  epsilon: number = ANGLE_EPSILON,
): boolean {
  const [dx0, dy0, dz0] = vec3Sub(x, y, z, x0, y0, z0);
  const [dx1, dy1, dz1] = vec3Sub(x, y, z, x1, y1, z1);

  return vec3Dot(dx0, dy0, dz0, dx1, dy1, dz1) < epsilon;
}

export function vec4Dist(
  x0: number,
  y0: number,
  z0: number,
  w0: number,
  x1: number,
  y1: number,
  z1: number,
  w1: number,
) {
  const dx = x0 - x1;
  const dy = y0 - y1;
  const dz = z0 - z1;
  const dw = w0 - w1;
  return sqrt(dx * dx + dy * dy + dz * dz + dw * dw);
}
